import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Image as ImageIcon, X } from "lucide-react";

const MAX = 5 * 1024 * 1024; // 5 MB

/**
 * Reusable image picker.
 * Props:
 *   value         Current image URL (public path or absolute).
 *   onChange(url) Called with the new URL after successful upload OR "" when cleared.
 *   label         Optional label above the field.
 *   testid        Optional data-testid prefix.
 */
export default function ImagePicker({ value, onChange, label = "Image", testid = "image-picker" }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const pickFile = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > MAX) { toast.error("Image is too large — max 5 MB"); return; }

    setUploading(true); setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); setProgress(0); }
  };

  return (
    <div>
      {label && <div className="vp-label mb-1.5" style={{ fontSize: "0.65rem" }}>{label}</div>}
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F1F3F7] border border-[#E4E6EC] flex items-center justify-center shrink-0">
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-6 h-6 text-[#B8BBC5]" strokeWidth={1.25} />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2">
            <button type="button" onClick={pickFile} disabled={uploading}
              className="vp-btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
              data-testid={testid + "-upload"}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />}
              {uploading ? `Uploading… ${progress}%` : (value ? "Replace" : "Upload image")}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="p-1.5 rounded-md text-[#4A4D57] hover:text-[#B00020] hover:bg-[#FDE8EA]" data-testid={testid + "-clear"}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            type="text"
            className="vp-input text-xs"
            value={value || ""}
            placeholder="…or paste an image URL"
            onChange={(ev) => onChange(ev.target.value)}
            data-testid={testid + "-url"}
          />
          <div className="text-xs text-[#747680]">PNG, JPG, WebP or GIF · max 5 MB</div>
          {uploading && (
            <div className="h-1.5 rounded-full bg-[#E4E6EC] overflow-hidden" data-testid={testid + "-progress"}>
              <div className="h-full bg-[#0B5CFF] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid={testid + "-file"} />
    </div>
  );
}
